// Item label taxonomy for the dashboard. Mirrors
// server/internal/dashboard/item_pricing.go ItemPricingTable — keep in sync.
// Used as a fallback display when the API response is missing name_th.
//
// bead: lucky-war-0ke (item sales endpoints, 2026-08-04)

export interface ItemLabel {
  name: string;
  unitPrice: number;
  unitLabel: string; // e.g. "ถ้วย", "คน", "ถุง", "หม้อ"
}

export const ITEM_LABEL_TH: Record<string, ItemLabel> = {
  SOUP003: { name: 'ซุปหม่าล่า', unitPrice: 29, unitLabel: 'ถ้วย' },
  SOUP004: { name: 'ซุปกระดูกหมู', unitPrice: 29, unitLabel: 'ถ้วย' },
  SOUP005: { name: 'ซุปหม่าล่า+ซุปกระดูกหมู', unitPrice: 29, unitLabel: 'ถ้วย' },
  SW00009: { name: 'หมั่นโถว', unitPrice: 29, unitLabel: 'ชุด' },
  // October 2026 re-price (lucky-war-7jsm): server is date-aware, September
  // windows keep 39/19 server-side; mirror carries the current window.
  BUFF011: { name: 'กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 49, unitLabel: 'คน' },
  BUFF012: { name: 'กุ้งแก้ว (เด็ก)', unitPrice: 24, unitLabel: 'คน' },
  SPE0053: { name: 'แคบหมู', unitPrice: 9, unitLabel: 'ถุง' },
  DSOUP07: { name: 'หม้อสุกี้ (เพิ่ม)', unitPrice: 50, unitLabel: 'หม้อ' },
  BUFF098: { name: 'ไอศกรีม (ผู้ใหญ่)', unitPrice: 39, unitLabel: 'คน' },
  BUFF099: { name: 'ไอศกรีม (เด็ก)', unitPrice: 19, unitLabel: 'คน' },
  BUFF116: { name: 'ซูชิ (ผู้ใหญ่)', unitPrice: 99, unitLabel: 'คน' },
  BUFF117: { name: 'ซูชิ (เด็ก)', unitPrice: 49, unitLabel: 'คน' },
  // อุดรธานี ซูชิ+ไอศกรีม combo (lucky_bbq brand only; mirrors server ItemPricingTable).
  BUFF122: { name: 'ซูชิ+ไอศกรีม (ผู้ใหญ่)', unitPrice: 138, unitLabel: 'คน' },
  BUFF123: { name: 'ซูชิ+ไอศกรีม (เด็ก)', unitPrice: 68, unitLabel: 'คน' },
  // ลัคกี้กุ้งค็อกเทล campaign (2026-08-05.., Lucky Suki). Add-on prices
  // (mirrors server ItemPricingTable; not the full buffet ticket).
  BUFF152: { name: 'กุ้งค็อกเทล (ผู้ใหญ่)', unitPrice: 99, unitLabel: 'คน' },
  BUFF153: { name: 'กุ้งค็อกเทล (เด็ก)', unitPrice: 49, unitLabel: 'คน' },
  BUFF155: { name: 'แก้ว+ค็อกเทล (ผู้ใหญ่)', unitPrice: 138, unitLabel: 'คน' },
  BUFF156: { name: 'แก้ว+ค็อกเทล (เด็ก)', unitPrice: 68, unitLabel: 'คน' },
  // กุ้งแก้ว+ค็อกเทล combo campaign 2026-08-12..23 (uplift over std 219/110).
  BUFF164: { name: 'กุ้งแก้ว+ค็อกเทล (ผู้ใหญ่)', unitPrice: 99, unitLabel: 'คน' },
  BUFF165: { name: 'กุ้งแก้ว+ค็อกเทล (เด็ก)', unitPrice: 49, unitLabel: 'คน' },
  // Union Mall-only buffet tickets launched 2026-08-25. Kid codes
  // BUFF171/BUFF174 were observed in POS on 2026-08-26..28 and roll into
  // their adult counterparts on the server. Reference uplifts only. The API
  // calculates these Union Mall dim-sum lines from the POS Item report's
  // actual Base Sales, not unitPrice × qty.
  BUFF167: { name: 'ติ่มซำ (ผู้ใหญ่)', unitPrice: 80, unitLabel: 'คน' },
  BUFF168: { name: 'ติ่มซำ (เด็ก)', unitPrice: 40, unitLabel: 'คน' }, // Nov sheet 299/149; POS-confirmed 2026-08-29+
  BUFF170: { name: 'ติ่มซำ+สุกี้ (ผู้ใหญ่)', unitPrice: 170, unitLabel: 'คน' },
  BUFF171: { name: 'ติ่มซำ+สุกี้ (เด็ก)', unitPrice: 85, unitLabel: 'คน' },
  BUFF173: { name: 'สุกี้+กุ้งแก้ว+ติ่มซำ (ผู้ใหญ่)', unitPrice: 219, unitLabel: 'คน' }, // Oct ref uplift, was 209
  BUFF174: { name: 'สุกี้+กุ้งแก้ว+ติ่มซำ (เด็ก)', unitPrice: 109, unitLabel: 'คน' }, // Oct ref uplift, was 104
  BUFF179: { name: 'สุกี้+ติ่มซำ+3 ซี้ซีฟู้ด (ผู้ใหญ่)', unitPrice: 299, unitLabel: 'คน' },
  // 2026-08-24..31 Lucky Suki campaign (uplift: tickets 348/173 − std 219/109).
  BUFF176: { name: 'กุ้งแก้ว+กุ้งค็อกเทล+ปลากระพง (ผู้ใหญ่)', unitPrice: 129, unitLabel: 'คน' },
  BUFF177: { name: 'กุ้งแก้ว+กุ้งค็อกเทล+ปลากระพง (เด็ก)', unitPrice: 64, unitLabel: 'คน' },
  // 2026-08-28..31 barramundi add-on campaigns (uplift: tickets 268/133 and
  // 307/152 − std 219/109).
  BUFF182: { name: 'ปลากระพง (ผู้ใหญ่)', unitPrice: 49, unitLabel: 'คน' },
  BUFF183: { name: 'ปลากระพง (เด็ก)', unitPrice: 24, unitLabel: 'คน' },
  BUFF185: { name: 'กุ้งแก้ว+ปลากะพง (ผู้ใหญ่)', unitPrice: 98, unitLabel: 'คน' }, // Oct re-price, was 88
  BUFF186: { name: 'กุ้งแก้ว+ปลากะพง (เด็ก)', unitPrice: 48, unitLabel: 'คน' }, // Oct re-price, was 43
  // ── September 2026 update sheet (ops drop 2026-09-22). Add-on/uplift
  // prices per head; Union Mall combos keep the same base-sales convention
  // as BUFF167..179 above (unitPrice is reference uplift, not revenue). ──
  SW00011: { name: 'หมั่นโถวทอดดิปนมข้ม', unitPrice: 29, unitLabel: 'ชุด' },
  REFI011: { name: 'ฟรีรีฟิ (ตั้งแต่ 2 ทุ่มขึ้นไป)', unitPrice: 0, unitLabel: 'คน' },
  DIM032: { name: 'บักกู๊ดเต๋', unitPrice: 99, unitLabel: 'ชุด' },
  BUFF079: { name: 'คู่หูคู่ดอง (ผู้ใหญ่)', unitPrice: 129, unitLabel: 'คน' },
  BUFF080: { name: 'คู่หูคู่ดอง (เด็ก)', unitPrice: 64, unitLabel: 'คน' },
  BUFF082: { name: 'คู่หูคู่ดอง+กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 168, unitLabel: 'คน' },
  BUFF083: { name: 'คู่หูคู่ดอง+กุ้งแก้ว (เด็ก)', unitPrice: 83, unitLabel: 'คน' },
  BUFF197: { name: 'คู่หูคู่ดอง+ปลากะพง (ผู้ใหญ่)', unitPrice: 178, unitLabel: 'คน' },
  BUFF198: { name: 'คู่หูคู่ดอง+ปลากะพง (เด็ก)', unitPrice: 88, unitLabel: 'คน' },
  BUFF200: { name: 'คู่หูคู่ดอง+กุ้งแก้ว+ปลากะพง (ผู้ใหญ่)', unitPrice: 217, unitLabel: 'คน' },
  BUFF201: { name: 'คู่หูคู่ดอง+กุ้งแก้ว+ปลากะพง (เด็ก)', unitPrice: 107, unitLabel: 'คน' },
  BUFF188: { name: 'สุกี้+ติ่มซำ+ปลากะพง (ผู้ใหญ่)', unitPrice: 219, unitLabel: 'คน' },
  BUFF189: { name: 'สุกี้+ติ่มซำ+ปลากะพง (เด็ก)', unitPrice: 109, unitLabel: 'คน' },
  BUFF191: { name: 'สุกี้+ติ่มซำ+ปลากะพง+กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 258, unitLabel: 'คน' },
  BUFF192: { name: 'สุกี้+ติ่มซำ+ปลากะพง+กุ้งแก้ว (เด็ก)', unitPrice: 128, unitLabel: 'คน' },
  BUFF194: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 338, unitLabel: 'คน' },
  BUFF195: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+กุ้งแก้ว (เด็ก)', unitPrice: 168, unitLabel: 'คน' },
  BUFF203: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง (ผู้ใหญ่)', unitPrice: 299, unitLabel: 'คน' },
  BUFF204: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง (เด็ก)', unitPrice: 149, unitLabel: 'คน' },
  BUFF206: { name: 'ติ่มซำ+คู่หูคู่ดอง (ผู้ใหญ่)', unitPrice: 209, unitLabel: 'คน' },
  BUFF207: { name: 'ติ่มซำ+คู่หูคู่ดอง (เด็ก)', unitPrice: 104, unitLabel: 'คน' },
  BUFF209: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+ปลากะพง (ผู้ใหญ่)', unitPrice: 348, unitLabel: 'คน' },
  BUFF210: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+ปลากะพง (เด็ก)', unitPrice: 173, unitLabel: 'คน' },
  BUFF212: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+กุ้งแก้ว+ปลากะพง (ผู้ใหญ่)', unitPrice: 387, unitLabel: 'คน' },
  BUFF213: { name: 'สุกี้+ติ่มซำ+คู่หูคู่ดอง+กุ้งแก้ว+ปลากะพง (เด็ก)', unitPrice: 192, unitLabel: 'คน' },
  BUFF033: { name: 'คู่หูคู่ดอง (ผู้ใหญ่)', unitPrice: 129, unitLabel: 'คน' },
  BUFF034: { name: 'คู่หูคู่ดอง (เด็ก)', unitPrice: 64, unitLabel: 'คน' },
  BUFF105: { name: 'คู่หูคู่ดอง+ไอศกรีม (ผู้ใหญ่)', unitPrice: 168, unitLabel: 'คน' },
  BUFF106: { name: 'คู่หูคู่ดอง+ไอศกรีม (เด็ก)', unitPrice: 83, unitLabel: 'คน' },
  BUFF119: { name: 'คู่หูคู่ดอง+ซูชิ (ผู้ใหญ่)', unitPrice: 228, unitLabel: 'คน' },
  BUFF120: { name: 'คู่หูคู่ดอง+ซูชิ (เด็ก)', unitPrice: 113, unitLabel: 'คน' },
  BUFF263: { name: 'คู่หูคู่ดอง+ซูชิ+ไอศกรีม (ผู้ใหญ่)', unitPrice: 267, unitLabel: 'คน' },
  BUFF264: { name: 'คู่หูคู่ดอง+ซูชิ+ไอศกรีม (เด็ก)', unitPrice: 132, unitLabel: 'คน' },
  BUFF101: { name: 'กุ้งแช่ (ผู้ใหญ่)', unitPrice: 69, unitLabel: 'คน' },
  BUFF102: { name: 'กุ้งแช่ (เด็ก)', unitPrice: 32, unitLabel: 'คน' },
  BUFF104: { name: 'กุ้งแก้ว+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 108, unitLabel: 'คน' },
  BUFF108: { name: 'กุ้งแก้ว+กุ้งแช่ (เด็ก)', unitPrice: 53, unitLabel: 'คน' },
  BUFF215: { name: 'กุ้งแก้ว+ปลากะพง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 157, unitLabel: 'คน' },
  BUFF216: { name: 'กุ้งแก้ว+ปลากะพง+กุ้งแช่ (เด็ก)', unitPrice: 77, unitLabel: 'คน' },
  BUFF218: { name: 'กุ้งแก้ว+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 237, unitLabel: 'คน' },
  BUFF219: { name: 'กุ้งแก้ว+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 117, unitLabel: 'คน' },
  BUFF221: { name: 'กุ้งแก้ว+ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 286, unitLabel: 'คน' },
  BUFF222: { name: 'กุ้งแก้ว+ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 141, unitLabel: 'คน' },
  BUFF224: { name: 'ปลากะพง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 118, unitLabel: 'คน' },
  BUFF225: { name: 'ปลากะพง+กุ้งแช่ (เด็ก)', unitPrice: 58, unitLabel: 'คน' },
  BUFF227: { name: 'ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 247, unitLabel: 'คน' },
  BUFF228: { name: 'ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 122, unitLabel: 'คน' },
  BUFF230: { name: 'คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 198, unitLabel: 'คน' },
  BUFF231: { name: 'คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 98, unitLabel: 'คน' },
  BUFF233: { name: 'ติ่มซำ+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 149, unitLabel: 'คน' },
  BUFF234: { name: 'ติ่มซำ+กุ้งแช่ (เด็ก)', unitPrice: 74, unitLabel: 'คน' },
  BUFF236: { name: 'ติ่มซำ+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 278, unitLabel: 'คน' },
  BUFF237: { name: 'ติ่มซำ+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 138, unitLabel: 'คน' },
  BUFF239: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 278, unitLabel: 'คน' },
  BUFF240: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+กุ้งแช่ (เด็ก)', unitPrice: 138, unitLabel: 'คน' },
  BUFF242: { name: 'สุกี้+ติ่มซำ+ปลากะพง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 288, unitLabel: 'คน' },
  BUFF243: { name: 'สุกี้+ติ่มซำ+ปลากะพง+กุ้งแช่ (เด็ก)', unitPrice: 143, unitLabel: 'คน' },
  BUFF245: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 456, unitLabel: 'คน' },
  BUFF246: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+ปลากะพง+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 226, unitLabel: 'คน' },
  BUFF272: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 407, unitLabel: 'คน' },
  BUFF273: { name: 'สุกี้+ติ่มซำ+กุ้งแก้ว+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 202, unitLabel: 'คน' },
  BUFF278: { name: 'สุกี้+ติ่มซำ+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 239, unitLabel: 'คน' }, // observed in POS 2026-09-22..27, not on sheet
  BUFF248: { name: 'กุ้งแช่ (ผู้ใหญ่)', unitPrice: 69, unitLabel: 'คน' },
  BUFF249: { name: 'กุ้งแช่ (เด็ก)', unitPrice: 34, unitLabel: 'คน' },
  BUFF251: { name: 'ไอศกรีม+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 108, unitLabel: 'คน' },
  BUFF252: { name: 'ไอศกรีม+กุ้งแช่ (เด็ก)', unitPrice: 53, unitLabel: 'คน' },
  BUFF254: { name: 'ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 237, unitLabel: 'คน' },
  BUFF255: { name: 'ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 117, unitLabel: 'คน' },
  BUFF257: { name: 'คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 198, unitLabel: 'คน' },
  BUFF258: { name: 'คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 98, unitLabel: 'คน' },
  BUFF260: { name: 'ซูชิ+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 168, unitLabel: 'คน' },
  BUFF261: { name: 'ซูชิ+กุ้งแช่ (เด็ก)', unitPrice: 83, unitLabel: 'คน' },
  BUFF266: { name: 'ซูชิ+ไอศกรีม+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 207, unitLabel: 'คน' },
  BUFF267: { name: 'ซูชิ+ไอศกรีม+กุ้งแช่ (เด็ก)', unitPrice: 102, unitLabel: 'คน' },
  BUFF269: { name: 'ซูชิ+ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 336, unitLabel: 'คน' },
  BUFF270: { name: 'ซูชิ+ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 166, unitLabel: 'คน' },
  BUFF275: { name: 'ซูชิ+คู่หูคู่ดอง+กุ้งแช่ (ผู้ใหญ่)', unitPrice: 297, unitLabel: 'คน' },
  BUFF276: { name: 'ซูชิ+คู่หูคู่ดอง+กุ้งแช่ (เด็ก)', unitPrice: 147, unitLabel: 'คน' },
  // ── October 2026 sheet (bead lucky-war-7jsm). The SERVER resolves prices
  // per period (lookupItemPriceForDate), so September windows keep their own
  // prices; this mirror carries the CURRENT (October) window only. ──
  BUFF007: { name: 'หมู Half (ผู้ใหญ่)', unitPrice: 49, unitLabel: 'คน' },
  BUFF008: { name: 'หมู Half (เด็ก)', unitPrice: 24, unitLabel: 'คน' },
  BUFF036: { name: 'หมู Half+กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 98, unitLabel: 'คน' },
  BUFF038: { name: 'หมู Half+กุ้งแก้ว (เด็ก)', unitPrice: 48, unitLabel: 'คน' },
  BUFF037: { name: 'หมู Half+กุ้งแก้ว (เด็ก)', unitPrice: 48, unitLabel: 'คน' }, // ongoing kid code; POS recoded BUFF038→BUFF037 on 2026-10-18
  BUFF281: { name: 'ปลากะพง+หมู Half (ผู้ใหญ่)', unitPrice: 98, unitLabel: 'คน' },
  BUFF282: { name: 'ปลากะพง+หมู Half (เด็ก)', unitPrice: 48, unitLabel: 'คน' },
  BUFF284: { name: 'ปลากะพง+กุ้งแก้ว+หมู Half (ผู้ใหญ่)', unitPrice: 147, unitLabel: 'คน' },
  BUFF285: { name: 'ปลากะพง+กุ้งแก้ว+หมู Half (เด็ก)', unitPrice: 72, unitLabel: 'คน' },
  BUFF287: { name: 'ติ่มซำ+หมู Half (ผู้ใหญ่)', unitPrice: 129, unitLabel: 'คน' },
  BUFF288: { name: 'ติ่มซำ+หมู Half (เด็ก)', unitPrice: 64, unitLabel: 'คน' },
  BUFF290: { name: 'ติ่มซำ+สุกี้+หมู Half (ผู้ใหญ่)', unitPrice: 219, unitLabel: 'คน' },
  BUFF291: { name: 'ติ่มซำ+สุกี้+หมู Half (เด็ก)', unitPrice: 109, unitLabel: 'คน' },
  BUFF293: { name: 'ติ่มซำ+สุกี้+หมู Half+กุ้งแก้ว (ผู้ใหญ่)', unitPrice: 268, unitLabel: 'คน' },
  BUFF294: { name: 'ติ่มซำ+สุกี้+หมู Half+กุ้งแก้ว (เด็ก)', unitPrice: 133, unitLabel: 'คน' },
  BUFF296: { name: 'ติ่มซำ+สุกี้+ปลากะพง+หมู Half (ผู้ใหญ่)', unitPrice: 268, unitLabel: 'คน' },
  BUFF297: { name: 'ติ่มซำ+สุกี้+ปลากะพง+หมู Half (เด็ก)', unitPrice: 133, unitLabel: 'คน' },
  BUFF299: { name: 'ติ่มซำ+สุกี้+ปลากะพง+กุ้งแก้ว+หมู Half (ผู้ใหญ่)', unitPrice: 317, unitLabel: 'คน' },
  BUFF300: { name: 'ติ่มซำ+สุกี้+ปลากะพง+กุ้งแก้ว+หมู Half (เด็ก)', unitPrice: 157, unitLabel: 'คน' },
  BUFF302: { name: 'หมู Half (ผู้ใหญ่)', unitPrice: 49, unitLabel: 'คน' },
  BUFF303: { name: 'หมู Half (เด็ก)', unitPrice: 24, unitLabel: 'คน' },
  BUFF305: { name: 'หมู Half+ไอศกรีม (ผู้ใหญ่)', unitPrice: 88, unitLabel: 'คน' },
  BUFF306: { name: 'หมู Half+ไอศกรีม (เด็ก)', unitPrice: 43, unitLabel: 'คน' },
  BUFF308: { name: 'หมู Half+ซูชิ (ผู้ใหญ่)', unitPrice: 148, unitLabel: 'คน' },
  BUFF309: { name: 'หมู Half+ซูชิ (เด็ก)', unitPrice: 73, unitLabel: 'คน' },
  BUFF311: { name: 'หมู Half+ไอศกรีม+ซูชิ (ผู้ใหญ่)', unitPrice: 187, unitLabel: 'คน' },
  BUFF312: { name: 'หมู Half+ไอศกรีม+ซูชิ (เด็ก)', unitPrice: 92, unitLabel: 'คน' },
  BUFF314: { name: 'กุ้งแช่น้ำปลา+หมู Half (ผู้ใหญ่)', unitPrice: 113, unitLabel: 'คน' }, // sheet verbatim (component sum 118)
  BUFF315: { name: 'กุ้งแช่น้ำปลา+หมู Half (เด็ก)', unitPrice: 58, unitLabel: 'คน' },
  BUFF317: { name: 'กุ้งแช่น้ำปลา+ไอศกรีม+หมู Half (ผู้ใหญ่)', unitPrice: 157, unitLabel: 'คน' },
  BUFF318: { name: 'กุ้งแช่น้ำปลา+ไอศกรีม+หมู Half (เด็ก)', unitPrice: 77, unitLabel: 'คน' },
  BUFF320: { name: 'คู่หูคู่ดอง+กุ้งแช่น้ำปลา+หมู Half (ผู้ใหญ่)', unitPrice: 247, unitLabel: 'คน' },
  BUFF321: { name: 'คู่หูคู่ดอง+กุ้งแช่น้ำปลา+หมู Half (เด็ก)', unitPrice: 122, unitLabel: 'คน' },
  BUFF323: { name: 'ซูชิ+หมู Half+กุ้งแช่น้ำปลา (ผู้ใหญ่)', unitPrice: 217, unitLabel: 'คน' },
  BUFF324: { name: 'ซูชิ+หมู Half+กุ้งแช่น้ำปลา (เด็ก)', unitPrice: 107, unitLabel: 'คน' },
  BUFF326: { name: 'ซูชิ+ไอศกรีม+หมู Half+กุ้งแช่น้ำปลา (ผู้ใหญ่)', unitPrice: 256, unitLabel: 'คน' },
  BUFF327: { name: 'ซูชิ+ไอศกรีม+หมู Half+กุ้งแช่น้ำปลา (เด็ก)', unitPrice: 126, unitLabel: 'คน' },
  BUFF329: { name: 'ซูชิ+ไอศกรีม+หมู Half+คู่หูคู่ดอง+กุ้งแช่น้ำปลา (ผู้ใหญ่)', unitPrice: 385, unitLabel: 'คน' },
  BUFF330: { name: 'ซูชิ+ไอศกรีม+หมู Half+คู่หูคู่ดอง+กุ้งแช่น้ำปลา (เด็ก)', unitPrice: 190, unitLabel: 'คน' },
  BUFF332: { name: 'คู่หูคู่ดอง+หมู Half (ผู้ใหญ่)', unitPrice: 178, unitLabel: 'คน' },
  BUFF333: { name: 'คู่หูคู่ดอง+หมู Half (เด็ก)', unitPrice: 88, unitLabel: 'คน' },
  BUFF335: { name: 'ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่น้ำปลา+หมู Half (ผู้ใหญ่)', unitPrice: 286, unitLabel: 'คน' },
  BUFF336: { name: 'ไอศกรีม+คู่หูคู่ดอง+กุ้งแช่น้ำปลา+หมู Half (เด็ก)', unitPrice: 141, unitLabel: 'คน' },
};

const FMT = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 });

export function formatBaht(n: number): string {
  return `฿${FMT.format(n)}`;
}

export function formatQty(n: number): string {
  return FMT.format(n);
}
